import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import styles from './Field.module.scss';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  readonly label?: ReactNode;
  readonly hint?: ReactNode;
  readonly error?: ReactNode;
  readonly required?: boolean;
  readonly leadingIcon?: ReactNode;
  readonly trailingIcon?: ReactNode;
  readonly wrapperClassName?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    required,
    leadingIcon,
    trailingIcon,
    className,
    wrapperClassName,
    id,
    ...rest
  },
  ref,
) {
  const controlClass = [
    styles.control,
    error ? styles.invalid : '',
    leadingIcon ? styles.hasLeading : '',
    trailingIcon ? styles.hasTrailing : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <div className={styles.inputGroup}>
      {leadingIcon ? <span className={styles.leadingIcon}>{leadingIcon}</span> : null}
      <input ref={ref} id={id} className={controlClass} required={required} {...rest} />
      {trailingIcon ? <span className={styles.trailingIcon}>{trailingIcon}</span> : null}
    </div>
  );

  if (!label && !hint && !error) {
    return content;
  }

  return (
    <div className={[styles.field, wrapperClassName ?? ''].join(' ')}>
      {label ? (
        <div className={styles.labelRow}>
          <label className={styles.label} htmlFor={id}>
            {label}
          </label>
          {required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
        </div>
      ) : null}
      {content}
      {hint && !error ? <span className={styles.hint}>{hint}</span> : null}
      {error ? <span className={styles.error}>{error}</span> : null}
    </div>
  );
});
